import { Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { RouteObject, useNavigate } from "react-router";

import * as Sentry from "@sentry/react";

import { EditChecklistPageSkeleton } from "@/components/Procedures/Components/Skeletons/EditChecklistPageSkeleton";
import { NodeDiagramPage } from "@/components/Procedures/NodeDiagram/Pages/NodeDiagramPage";
import ProcedureComparisonPage from "@/components/Procedures/Pages/ProcedureComparisonPage";
import { Procedure } from "@/components/Procedures/Pages/ProcedurePage";
import ProceduresPage from "@/components/Procedures/Pages/ProceduresPage";
import { Procedures } from "@/components/Procedures/Procedures";
import { ShowUpdatedProcedures } from "@/components/Procedures/ShowUpdatedProcedures";
import ProcedureUpdates from "@/components/Task/ProcedureUpdates";

import PageError from "@/helpers/components/Errors/PageError";

export const ProcedureRoutes: RouteObject[] = [
	{
		path: "procedures/*",
		element: (
			<Sentry.ErrorBoundary
				fallback={({ error, componentStack, resetError }) => (
					<PageError
						error={error}
						componentStack={componentStack}
						reset={resetError}
					/>
				)}
			>
				<ProceduresPage />
			</Sentry.ErrorBoundary>
		),
		children: [
			{
				index: true,
				element: (
					<Sentry.ErrorBoundary
						fallback={({ error, componentStack, resetError }) => (
							<PageError
								error={error}
								componentStack={componentStack}
								reset={resetError}
							/>
						)}
					>
						<Procedures />
					</Sentry.ErrorBoundary>
				),
			},
			{
				path: "procedureUpdates",
				children: [
					{
						path: "newImprov",
						element: <ProcedureUpdates page="newImprov" />,
					},
					{
						path: "myImprov",
						element: <ProcedureUpdates page="myImprov" />,
					},
					{
						path: "verified",
						element: <ProcedureUpdates page="verified" />,
					},
					{
						path: "declined",
						element: <ProcedureUpdates page="declined" />,
					},
				],
			},
			{
				path: "compareProcedures",
				element: <ShowUpdatedProcedures />,
			},
			{
				path: ":id",
				element: <Procedure />,
			},
		],
	},
	{
		path: "procedure/:id/*",
		element: (
			<Sentry.ErrorBoundary
				fallback={({ error, componentStack, resetError }) => (
					<PageError
						error={error}
						componentStack={componentStack}
						reset={resetError}
					/>
				)}
			>
				<ProceduresPage />
			</Sentry.ErrorBoundary>
		), // Ensure ProceduresPage handles tabs
		children: [
			{
				index: true,
				element: <Procedure />,
			},
			{
				path: "checklist/:schemeId",
				element: <Procedure />,
			},
			{
				path: "checklist/:schemeId/edit",
				lazy: async () => {
					const { ManualChecklistEditorPage: EditChecklistPage } = await import(
						"@/components/Procedures/Pages/ChecklistEditorPage"
					);
					return {
						Component: () => (
							<ErrorBoundary
								FallbackComponent={PageError}
								onReset={() => {
									const navigate = useNavigate();
									navigate(-1);
								}}
							>
								<Suspense fallback={<EditChecklistPageSkeleton />}>
									<EditChecklistPage />
								</Suspense>
							</ErrorBoundary>
						),
					};
				},
			},
			{
				path: "node/:schemeId/edit",
				element: <Procedure />,
			},
			{
				path: "node/:groupId/CompareVersions",
				element: (
					<Sentry.ErrorBoundary
						fallback={({ error, componentStack, resetError }) => (
							<PageError
								error={error}
								componentStack={componentStack}
								reset={resetError}
							/>
						)}
					>
						<ProcedureComparisonPage />
					</Sentry.ErrorBoundary>
				),
			},
		],
	},
	{
		path: "procedure/:id/nodeDiagram",
		element: (
			<Sentry.ErrorBoundary
				fallback={({ error, componentStack, resetError }) => (
					<PageError
						error={error}
						componentStack={componentStack}
						reset={resetError}
					/>
				)}
			>
				<NodeDiagramPage />
			</Sentry.ErrorBoundary>
		),
	},
];
